/**
 * @fileoverview Singer menu component.
 * @summary Burger menu for singers with navigation options.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/singer/components/SingerMenu
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../hooks/redux.js'
import { logout } from '../../../store/slices/authSlice.js'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBars,
  faChartLine,
  faCog,
  faCircleInfo,
  faRightFromBracket,
  faShieldAlt,
} from '@fortawesome/free-solid-svg-icons'

/**
 * @summary Menu item interface.
 */
interface MenuItem {
  id: string
  label: string
  icon: typeof faBars
  path?: string
  action?: () => void
  divider?: boolean
}

/**
 * @summary Singer menu component.
 * @returns {React.ReactElement} Singer menu component.
 */
export default function SingerMenu() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAppSelector((state) => state.auth)
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Check if guest mode
  const guestSessionToken = localStorage.getItem('guestSessionToken')
  const guestPseudo = localStorage.getItem('guestPseudo')
  const isGuest = !isAuthenticated && !!guestSessionToken

  const displayName = isAuthenticated
    ? user?.profile?.pseudo || user?.profile?.firstName || user?.email
    : guestPseudo || t('singer.guestMode')

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleLogout = async () => {
    if (isAuthenticated) {
      // Authenticated user logout
      try {
        await dispatch(logout()).unwrap()
        navigate('/', { replace: true })
      } catch (error) {
        navigate('/login', { replace: true })
      }
    } else {
      // Guest logout - clear localStorage
      localStorage.removeItem('guestSessionToken')
      localStorage.removeItem('guestPseudo')
      localStorage.removeItem('guestEventId')
      localStorage.removeItem('guestEventName')
      localStorage.removeItem('guestEventDate')
      localStorage.removeItem('guestEventStatus')
      navigate('/', { replace: true })
    }
    setIsOpen(false)
  }

  const handleNavigate = (path: string) => {
    navigate(path)
    setIsOpen(false)
  }

  // Menu items for authenticated singers
  const authenticatedMenuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: t('singer.dashboard'),
      icon: faChartLine,
      path: '/singer/dashboard',
    },
    {
      id: 'settings',
      label: t('singer.settings'),
      icon: faCog,
      path: '/singer/settings',
    },
    {
      id: 'data-privacy',
      label: t('singerMenu.dataPrivacy'),
      icon: faShieldAlt,
      path: '/data-privacy',
    },
    {
      id: 'about',
      label: t('singerMenu.about'),
      icon: faCircleInfo,
      path: '/about',
    },
    {
      id: 'divider1',
      label: '',
      icon: faBars,
      divider: true,
    },
    {
      id: 'logout',
      label: t('auth.logout'),
      icon: faRightFromBracket,
      action: handleLogout,
    },
  ]

  // Menu items for guest singers
  const guestMenuItems: MenuItem[] = [
    {
      id: 'logout',
      label: t('singer.leaveEvent'),
      icon: faRightFromBracket,
      action: handleLogout,
    },
  ]

  const menuItems = isAuthenticated ? authenticatedMenuItems : guestMenuItems

  // Don't render if not authenticated and not guest
  if (!isAuthenticated && !isGuest) {
    return null
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Burger menu button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center w-10 h-10 rounded-lg text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-(--color-bg-tertiary) transition-colors"
        aria-label={t('singer.menu')}
        title={displayName}
      >
        <FontAwesomeIcon icon={faBars} className="w-5 h-5" />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 z-50 mt-2 w-56 bg-(--color-bg-secondary) rounded-lg shadow-2xl">
            <div className="py-1">
              {/* User info header */}
              <div className="px-4 py-3 border-b border-(--color-border)">
                <p className="text-sm font-medium text-(--color-text-primary) truncate">
                  {displayName}
                </p>
                {isGuest && (
                  <p className="text-xs text-(--color-text-secondary)">
                    ({t('singer.guestMode')})
                  </p>
                )}
              </div>

              {/* Menu items */}
              {menuItems.map((item) => {
                if (item.divider) {
                  return (
                    <div key={item.id} className="my-1 border-t border-(--color-border)"></div>
                  )
                }

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      if (item.action) {
                        item.action()
                      } else if (item.path) {
                        handleNavigate(item.path)
                      }
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-(--color-text-secondary) hover:bg-(--color-bg-tertiary) hover:text-(--color-text-primary) transition-colors"
                  >
                    <FontAwesomeIcon icon={item.icon} className="w-4 h-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

